// Which session a sign-in-required viewer action (wishlist, store follow — M-D9) acts on. Kept free of React
// so the race-sensitive part is unit-tested.
import type { QueryClient } from "@tanstack/react-query";
import type { Session } from "../types";
import { qk } from "./qk";
import { sessionQuery } from "./session";

/**
 * The session to act on. A cached authenticated session is trusted without a round trip (if it has gone
 * stale the server answers 401 and the caller redirects then); an absent or anonymous one is re-read, since
 * the visitor may have signed in elsewhere. Returns null when the session can't be read, so a transient
 * failure never passes for "signed out".
 */
export async function resolveViewerSession(queryClient: QueryClient): Promise<Session | null> {
  const cached = queryClient.getQueryData<Session>(qk.session());
  if (cached?.status === "authenticated") return cached;
  try {
    return await queryClient.fetchQuery({ ...sessionQuery(), staleTime: 0 });
  } catch {
    return null;
  }
}

export type ViewerToggleDecision<TNext> =
  | { kind: "session-error" }
  | { kind: "join" }
  | { kind: "claim" }
  | { kind: "toggle"; next: TNext };

/** What a sign-in-required toggle does, given the resolved session and the value read after resolving it. */
export function decideViewerToggle(session: Session | null, currentlyOn: boolean): ViewerToggleDecision<boolean> {
  if (!session) return { kind: "session-error" };
  if (session.status !== "authenticated") return { kind: "join" };
  if (!session.person) return { kind: "claim" };
  return { kind: "toggle", next: !currentlyOn };
}
